/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, ViewChild, OnDestroy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { Subject, Observable, of, combineLatest } from 'rxjs';
import {
	map,
	takeUntil,
	filter,
	distinctUntilChanged,
	withLatestFrom,
	delay,
	switchMap,
} from 'rxjs/operators';
import { EditType } from '../../edit-type.directive';
import { TicketEditService } from '../../ticket-edit.service';
import { EditTicketsCombined } from 'src/app/app-data/models/EditTicketsCombined';

import { TemplateType } from '../../interfaces/TemplateType';
import { trigger, transition, style, animate, state, keyframes, sequence } from '@angular/animations';

import { getNested } from 'utils/basic-utils';
import { cloneStack, PromisePin } from '@touchplan/common';
import { PlanState } from 'src/app/services/plan-state.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { ProjectMember } from '@touchplan/common';
import { PositionTrackerDirective } from 'src/app/common/utility-components/position-tracker/position-tracker.directive';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

interface PromisePanelOutput {
	id?: string;
	varianceId?: string;
	varianceComment?: string;
	plannedFinish: string;
}

@Component({
	selector: 'ticket-edit-wrapper',
	templateUrl: './edit-wrapper.component.html',
	host: {
		'[class.actual-ticket-edit-wrapper-empty]': 'ticketEditWrapperEmpty',
	},
	//this animation is horrifically hacky, just to give a slight delay to the close on duplication
	//it only works by adding special handling based around the save state. Including null ops, arbitrary delays
	//and a settimeout in the event handling. Near as I've been able to tell, there are basically no hooks for sequencing
	//a :leave of one item and an :enter of another item.
	//If ever asked to improve this, you'll need coordination between the save state, a new state for debouncing managed internally
	// and something to combine them. May want a complete overhaul. Have fun.
	animations: [
		trigger('scaleInOut', [
			state('void', style({ transform: 'scale(0)', transformOrigin: 'bottom center', opacity: 0 })),
			state('*', style({ transform: 'scale(1)', transformOrigin: 'bottom center', opacity: 1 })),
			state('save', style({ transform: 'scale(1)', transformOrigin: 'bottom center' })),
			transition('void => save', animate('250ms 300ms ease-in')),
			transition('void => *', animate('250ms ease-in')),
			transition('save => void', [
				sequence([
					animate(
						'200ms ease-in-out',
						keyframes([
							style({ filter: 'brightness(1)', offset: 0 }),
							style({ filter: 'brightness(2)', offset: 0.5 }),
							style({ filter: 'brightness(1)', offset: 1 }),
						])
					),
					animate('250ms ease-out'),
				]),
			]),
			transition('save => null', []),
			transition('* => void', animate('250ms')),
			transition('* => save', animate('250ms')),
		]),
	],
	providers: [LoggerServiceFactory.provide('EditWrapperComponent')],
	// Experimental "animation on parent version"
	// animations: [
	// 	trigger('scaleInOut', [
	// 		transition('* <=> *', [
	// 			query(':enter', [
	// 				style({ transform: 'scale(0)', transformOrigin: 'bottom center', opacity: 0 }),
	// 				stagger(100, [
	// 					animate('250ms ease-in'),
	// 					style({ transform: 'scale(1)', transformOrigin: 'bottom center', opacity: 1 })
	// 				])
	// 			], { optional: true}),
	// 			query(':leave', [
	// 				style({ transform: 'scale(1)', transformOrigin: 'bottom center', opacity: 1 }),
	// 				stagger(-100, [
	// 					animate('250ms'),
	// 					style({ transform: 'scale(0)', transformOrigin: 'bottom center', opacity: 0 }),
	// 				]),
	// 			], { optional: true})
	// 		])
	// 	]),
	// ]
	// changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditWrapperComponent implements AfterViewInit, OnDestroy {
	constructor(
		public ticketEditService: TicketEditService,
		protected planState: PlanState,
		private planAccessCheck: PlanAccessCheck,
		private cd: ChangeDetectorRef,
		private featureFlagManager: FeatureFlagManager,
		private logger: DefaultLoggerService
	) {
		this.logger.log('EditWrapperComponent');
		this.dockedTicketEnabled$ = this.featureFlagManager.getFlag$(
			DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED
		);
	}
	@ViewChild(EditType) public editType: EditType;

	public TemplateType = TemplateType;

	public editData: EditTicketsCombined;
	public shutdownSubject: Subject<boolean> = new Subject();

	public promiseStack: any;
	public rfiId: any;
	public ticketType: any;
	public assignedProjectMember: Observable<ProjectMember>;
	public shouldDisplayEditWrapper$: Observable<boolean>;
	private dockedTicketEnabled$: Observable<boolean>;

	public isAdmin$ = this.planState.authedProjectMember$.pipe(
		map((authedProjectMember) =>
			authedProjectMember ? authedProjectMember.view.accessLevel === 'Admin' : false
		)
	);

	public hasAcce$$ = this.planAccessCheck
		.hasAccessToTicket$(
			this.ticketEditService.combinedEdit$.pipe(
				map((edit) => {
					if (!edit || !edit.data || edit.selectedList.size === 0) {
						return null;
					}
					return edit.data;
				})
			)
		)
		.pipe(distinctUntilChanged());

	public editTicketFinish$ = this.ticketEditService.combinedEdit$.pipe(
		map((edit) => (edit && edit.data ? edit.data.liveFinish : '')),
		distinctUntilChanged()
	);
	public editTicketIsActual$ = this.ticketEditService.combinedEdit$.pipe(
		map((edit) => edit && edit.data && edit.data.isActualized),
		distinctUntilChanged()
	);

	private flashState$: Observable<string>;
	public flashStateSync;

	public setToNullTimestamp;

	public ngAfterViewInit() {
		this.promiseStack = this.ticketEditService.combinedEdit$.pipe(
			map((etc) => etc.data.livePromisePeriodStack)
		);
		this.rfiId = this.ticketEditService.combinedEdit$.pipe(map((etc) => etc.data.procoreRfiId));
		this.ticketType = this.ticketEditService.combinedEdit$.pipe(
			map((etc) => (etc ? etc.selectedType : null))
		);
		this.assignedProjectMember = this.ticketEditService.combinedEdit$.pipe(
			map((etc) => etc.data.assignedProjectMember)
		);
		this.flashState$ = this.ticketEditService.closeMethod$.pipe(
			filter((val) => val === 'save'),
			// tap(thing => this.logger.log("=== THING FIRED", thing)),
			takeUntil(this.shutdownSubject)
		);

		// this.ticketType.subscribe(t => {
		// 	this.flashStateSync = t;
		// 	this.cd.detectChanges();
		// });
		this.flashState$.subscribe((t) => {
			this.logger.log('animation flashState', t);
			this.flashStateSync = t;
			this.cd.detectChanges();
		});

		this.shouldDisplayEditWrapper$ = combineLatest([
			this.dockedTicketEnabled$,
			this.ticketEditService.ticketTemplate$,
			this.planState.tickets.selectedTicket$,
		]).pipe(
			map(([dockedTicketEnabled, ticketTemplate, selectedTickets]) => {
				// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7190
				// currently cycle ticket and multi edit use the old style edit panel. Update this when they are converted to docked ticket
				if (dockedTicketEnabled) {
					return (
						ticketTemplate === TemplateType.multiTask ||
						ticketTemplate === TemplateType.multiMilestone ||
						ticketTemplate === TemplateType.multiConstraint ||
						selectedTickets.list.size > 1
					);
				}
				return true;
				// END PLACEHOLDER REPLACE AS PART OF TP-7190
			}),
			takeUntil(this.shutdownSubject)
		);
	}

	public positionTrackerCallback = (positionTracker: PositionTrackerDirective) => {
		this.ticketEditService.ticketEditPanelIsDisplayed$
			.pipe(
				//grab the latest etc value
				withLatestFrom(this.ticketEditService.combinedEdit$),
				//throw it into a switchMap _specificially_ so that a user quicker than 300ms at opening an closing edit doesn't see weird stuff.
				//(switchmap will automatically cancel the previous delay if there's a newer value to set)
				switchMap((comb) => {
					return of(comb[0]).pipe(
						delay(300),
						//_and_ we're not interested in setting an edit ticket derived position when the edit ticket ui isn't in the dom anymore
						filter(() => comb[1].originalList.size > 0)
					);
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((displayed) => {
				positionTracker.reportPosition();
			});
	};

	public writePromises(promiseData: PromisePanelOutput[]) {
		if (!getNested(this.ticketEditService.combinedEdit$.getValue(), 'data.livePromisePeriodStack')) {
			return;
		}

		const ppStack = cloneStack(this.ticketEditService.combinedEdit$.getValue().data.livePromisePeriodStack);

		const stackToSave: PromisePin[] = [];

		promiseData.forEach((newP) => {
			const idx = newP.id ? ppStack.findIndex((oldP) => newP.id === oldP.$id) : -1;
			if (idx !== -1) {
				const oldP = ppStack.splice(idx, 1)[0];

				['plannedFinish', 'varianceId', 'varianceComment'].forEach((key) => {
					if (newP[key] !== undefined) {
						oldP.data[key] = newP[key];
					}
				});
				stackToSave.push(oldP);
			} else {
				//can add here if supported
			}
		});

		const output = { livePromisePeriodStack: stackToSave };

		this.logger.log('write it back----');
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.writeCombinationBackToList(
			this.ticketEditService.combinedEdit$.getValue(),
			output,
			false
		);
	}

	public revertPromises(pins: PromisePin[]) {
		const output = { livePromisePeriodStack: pins };
		this.ticketEditService.markFormAsDirty();
		this.ticketEditService.writeCombinationBackToList(
			this.ticketEditService.combinedEdit$.getValue(),
			output,
			false
		);
	}

	public closeOverlay(): void {
		this.ticketEditService.setOverlay(null);
	}

	public ngOnDestroy(): void {
		this.shutdownSubject.next(true);
	}

	public OnAnimationEvent(event) {
		// Add animation debugging here if you require it.
		// this.logger.log('---animation - ', event);
		//reset
		// if (event.fromState === 'save' && event.toState === 'void') {
		// 	this.flashStateSync = null;
		// }
		if (event.fromState === 'save' && event.toState === 'void') {
			clearTimeout(this.setToNullTimestamp);
			this.setToNullTimestamp = setTimeout(() => {
				this.flashStateSync = null;
			}, 300);
		}
	}
}
